import { X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'
import {
  useArchivePOs,
  useAllPOs,
  useLocations,
  useMergePOs,
  usePOCounts,
  usePOs,
} from '@/hooks/usePOs'
import { useBackorders } from '@/hooks/useBackorders'
import { useSuppliers } from '@/hooks/useVendors'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/store/uiStore'

const STATUS_TABS = [
  { value: undefined, label: 'All', countKey: 'ALL' as const },
  { value: 'DRAFT', label: 'Draft', countKey: 'DRAFT' as const },
  { value: 'AWAITING_APPROVAL', label: 'Awaiting', countKey: 'AWAITING_APPROVAL' as const },
  { value: 'APPROVED', label: 'Approved', countKey: 'APPROVED' as const },
  { value: 'ORDERED', label: 'Ordered', countKey: 'ORDERED' as const },
  { value: 'ARCHIVED', label: 'Archived', countKey: 'ARCHIVED' as const },
]

// Only ORDERED / PARTIAL_ORDERED rows can be archived by the backend's
// /archive endpoint; Archive Selected applies to that subset.
const ARCHIVABLE_STATUSES = new Set(['ORDERED', 'PARTIAL_ORDERED'])

export function POFilters() {
  const { filters, setFilters, selectedPOs, clearSelectedPOs, activeView, setActiveView } = useUIStore()
  const counts = usePOCounts()
  const { data: backorders = [] } = useBackorders('OPEN')
  const { data: locations = [] } = useLocations()
  const suppliers = useSuppliers()
  const { data: results = [] } = usePOs(filters)
  const { data: allPOs = [] } = useAllPOs()

  const archive = useArchivePOs()
  const merge = useMergePOs()

  const [localSearch, setLocalSearch] = useState(filters.search ?? '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setLocalSearch(filters.search ?? '')
  }, [filters.search])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      setLocalSearch(val)
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        setFilters({ search: val || undefined })
      }, 200)
    },
    [setFilters]
  )

  const clearSearch = useCallback(() => {
    setLocalSearch('')
    setFilters({ search: undefined })
  }, [setFilters])

  const selectedIds = Array.from(selectedPOs)
  const selectedRows = allPOs.filter((p) => selectedPOs.has(p.po_id))

  // Archive eligibility: at least 1 selected and all are ORDERED / PARTIAL_ORDERED.
  const archivableIds = selectedRows
    .filter((p) => ARCHIVABLE_STATUSES.has(p.status))
    .map((p) => p.po_id)
  const canArchive =
    selectedIds.length > 0 && archivableIds.length === selectedIds.length

  // Merge eligibility: 2+ selected, all DRAFT, same vendor + location.
  const canMerge = (() => {
    if (selectedRows.length < 2) return false
    const statuses = new Set(selectedRows.map((p) => p.status))
    if (statuses.size !== 1 || !statuses.has('DRAFT')) return false
    const vendors = new Set(selectedRows.map((p) => p.vendor))
    if (vendors.size !== 1) return false
    const locs = new Set(selectedRows.map((p) => p.location_to_receive ?? ''))
    return locs.size === 1
  })()

  const handleArchive = () => {
    if (!canArchive) return
    if (
      !window.confirm(
        `Archive ${selectedIds.length} purchase order${selectedIds.length === 1 ? '' : 's'}?`
      )
    ) {
      return
    }
    archive.mutate(selectedIds, {
      onSuccess: (res) => {
        clearSelectedPOs()
        if (res.failures.length === 0) {
          toast.success(`Archived ${res.archived} PO${res.archived === 1 ? '' : 's'}`)
        } else {
          toast.error(
            `Archived ${res.archived}; ${res.failures.length} failed (${res.failures.map((f) => f.po_id).join(', ')})`
          )
        }
      },
    })
  }

  const handleMerge = () => {
    if (!canMerge) return
    if (
      !window.confirm(
        `Merge ${selectedIds.length} DRAFT POs into one new DRAFT? Source POs will be deleted.`
      )
    ) {
      return
    }
    merge.mutate(selectedIds, {
      onSuccess: (newPO) => {
        clearSelectedPOs()
        toast.success(`Merged into ${newPO?.po_id ?? 'new PO'}`)
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Merge failed')
      },
    })
  }

  return (
    <div className="border-b border-[var(--line-warm)] bg-[var(--surface-card)]">
      {/* Status tabs */}
      <div className="flex h-9 items-stretch border-b border-[var(--line-warm)]">
        {STATUS_TABS.map((tab) => {
          const isActive = activeView === 'pos' && filters.status === tab.value
          const count = tab.countKey ? counts[tab.countKey] : undefined
          return (
            <button
              key={tab.label}
              type="button"
              onClick={() => {
                setActiveView('pos')
                setFilters({ status: tab.value })
              }}
              className={cn(
                'flex items-center gap-1.5 px-3.5 text-xs border-r border-[var(--line-warm)] transition-colors',
                isActive
                  ? 'text-[var(--text-primary)] font-semibold'
                  : 'text-[var(--ink-2)] hover:text-[var(--text-primary)]'
              )}
              style={
                isActive
                  ? { boxShadow: 'inset 0 -2px 0 var(--text-primary)' }
                  : undefined
              }
            >
              {tab.label}
              {count !== undefined && (
                <span className="text-[10px] text-[var(--mute)]">{count}</span>
              )}
            </button>
          )
        })}
        {/* Backorder / OOS list — a separate data source, not a PO status. */}
        <button
          type="button"
          onClick={() => setActiveView('backorder')}
          className={cn(
            'flex items-center gap-1.5 px-3.5 text-xs border-r border-[var(--line-warm)] transition-colors',
            activeView === 'backorder'
              ? 'text-[var(--text-primary)] font-semibold'
              : 'text-[var(--ink-2)] hover:text-[var(--text-primary)]'
          )}
          style={
            activeView === 'backorder'
              ? { boxShadow: 'inset 0 -2px 0 var(--text-primary)' }
              : undefined
          }
        >
          Backorder
          <span className="text-[10px] text-[var(--mute)]">{backorders.length}</span>
        </button>
      </div>

      {/* Toolbar: bulk actions on the left, search + supplier + location on the right.
          Hidden on the backorder view, which renders its own toolbar. */}
      {activeView === 'pos' && (
      <div className="flex items-center gap-2 px-4 py-2">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          disabled={!canArchive || archive.isPending}
          onClick={handleArchive}
          title={
            selectedIds.length === 0
              ? 'Select one or more Ordered POs to archive'
              : canArchive
                ? `Archive ${selectedIds.length} selected`
                : 'Only Ordered / Partial-ordered POs can be archived'
          }
        >
          {archive.isPending ? 'Archiving…' : 'Archive Selected'}
          {selectedIds.length > 0 && (
            <span className="text-[10px] text-[var(--mute)] ml-0.5">
              ({selectedIds.length})
            </span>
          )}
        </Button>

        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          disabled={!canMerge || merge.isPending}
          onClick={handleMerge}
          title={
            selectedIds.length < 2
              ? 'Select 2+ DRAFT POs with same vendor + location to merge'
              : canMerge
                ? `Merge ${selectedIds.length} DRAFT POs into one`
                : 'All selected POs must be DRAFT, same vendor, same location'
          }
        >
          {merge.isPending ? 'Merging…' : 'Merge Selected'}
          {selectedIds.length > 0 && (
            <span className="text-[10px] text-[var(--mute)] ml-0.5">
              ({selectedIds.length})
            </span>
          )}
        </Button>

        <div className="flex-1" />

        <div className="text-[11px] text-[var(--mute)]" aria-live="polite">
          {results.length} {results.length === 1 ? 'result' : 'results'}
        </div>

        {/* Search */}
        <div className="relative max-w-[260px] flex-1">
          <Input
            placeholder="Search PO #, vendor, SKU…"
            value={localSearch}
            onChange={handleSearchChange}
            className="h-7 pr-6 text-xs"
          />
          {localSearch && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--mute)] hover:text-[var(--text-primary)]"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Supplier filter */}
        <Select
          value={filters.supplier ?? 'all'}
          onValueChange={(v) =>
            setFilters({ supplier: v === 'all' ? undefined : v })
          }
        >
          <SelectTrigger
            size="sm"
            className={cn(
              'h-7 w-[160px] text-xs',
              filters.supplier && 'border-[var(--text-primary)] text-[var(--text-primary)]'
            )}
            aria-label="Filter by supplier"
          >
            <span data-slot="select-value">
              {filters.supplier ?? 'All suppliers'}
            </span>
          </SelectTrigger>
          <SelectContent position="popper" align="end" sideOffset={4}>
            <SelectItem value="all">All suppliers</SelectItem>
            {suppliers.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Location filter */}
        <Select
          value={filters.location ?? 'all'}
          onValueChange={(v) =>
            setFilters({ location: v === 'all' ? undefined : v })
          }
        >
          <SelectTrigger
            size="sm"
            className={cn(
              'h-7 w-[160px] text-xs',
              filters.location && 'border-[var(--text-primary)] text-[var(--text-primary)]'
            )}
            aria-label="Filter by location"
          >
            <span data-slot="select-value">
              {filters.location ?? 'All locations'}
            </span>
          </SelectTrigger>
          {/* `position="popper"` anchors the menu to the trigger; without it
              Radix Select defaults to `item-aligned` which can render the
              menu far below the page when no value is selected. */}
          <SelectContent position="popper" align="end" sideOffset={4}>
            <SelectItem value="all">All locations</SelectItem>
            {locations.map((loc) => (
              <SelectItem key={loc} value={loc}>
                {loc}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      )}
    </div>
  )
}
